/**
 * @template T
 * @param {{[key: string]: T}} prev
 * @param {[string, T]} param1
 */
export const toObject = (prev, [key, value]) => ({
    ...prev,
    [key]: value
});

/**
 * @template T
 * @param {T[]} array
 * @param {number} index
 * @param {T} newItem
 * @returns {T[]} shallow copy
 */
export const replaceItemAt = (array, index, newItem) => Object.assign(array.slice(), { [index]: newItem });

/**
 * @template T
 * @param {T[]} array
 * @param {number} index
 * @param {T} newItem
 * @returns {T[]} shallow copy of initial array containing the inserted item
 */
export const insertItemAt = (array, index, newItem) => {
    const shallowCopy = array.slice();
    shallowCopy.splice(index, 0, newItem);
    return shallowCopy;
};

/**
 * @template T
 * @param {T[]} array
 * @param {number} index
 * @returns {T[]} shallow copy of initial array
 */
export const removeItemAt = (array, index) => {
    const shallowCopy = array.slice();
    shallowCopy.splice(index, 1);
    return shallowCopy;
};

/**
 * Finds an element in a sorted array
 * @template T, U
 * @param {U[]} ar
 * @param {T} el
 * @param {(leftVal: T, rightVal: U) => number} compare_fn
 * @returns {number} index of found element
 * or a negative value (one's complement) indicating the insertion point for the new element if the element is not found
 */
export function binarySearch(ar, el, compare_fn) {
    var m = 0;
    var n = ar.length - 1;
    while (m <= n) {
        var k = (n + m) >> 1;
        var cmp = compare_fn(el, ar[k]);
        if (cmp > 0) {
            m = k + 1;
        } else if (cmp < 0) {
            n = k - 1;
        } else {
            return k;
        }
    }
    return -m - 1;
}

/**
 * @param {number} value
 * @returns {number}
 */
export const onesComplementIfNegative = (value) => (value < 0 ? ~value : value);

const EPSILON = 10e-4;
/**
 * compare with EPSILON margin
 * @param {number} a
 * @param {number} b
 * @returns
 */
export const compareWithEpsilon = (a, b) => (Math.abs(a - b) < EPSILON ? 0 : a - b);

export const compare = (a, b) => a - b;

/**
 * @template T
 * @param {T[]} arr
 * @returns
 */
export const duplicateLastItem = (arr) => {
    if (arr.length) {
        arr.push(arr[arr.length - 1]);
    }
    return arr;
};

/**
 * @template T
 * @param {{[K in keyof T]: T[K]}} obj
 */
export const duplicateLastItemOfEveryMember = (obj) => {
    Object.keys(obj).map((key) => duplicateLastItem(obj[key]));
};

/**
 * @template T
 * @param {T[]} arr
 */
export const pairwise = (arr) =>
    arr
        .map((item, idx) => /** @type {[current: T, next: T]} */ ([item, arr[idx + 1]]))
        .filter((item) => item[0] !== null && item[0] !== undefined && item[1] !== null && item[1] !== undefined);

/**
 * @template T
 * @param {T} a
 * @param {T} b
 */
const compareByReference = (a, b) => a === b;

/**
 * @template T
 * @param {T[]} arr1
 * @param {T[]} arr2
 * @param {(a: T, b: T) => boolean} [compare]
 */
export const shallowCompareArrays = (arr1, arr2, compare = compareByReference) => {
    let arraysAreEqual = arr1.length === arr2.length;
    for (let i = 0; i < arr1.length && arraysAreEqual; i++) {
        const areEqual = compare(arr1[i], arr2[i]);
        arraysAreEqual = arraysAreEqual && areEqual;
    }

    return arraysAreEqual;
};

/**
 * @template T
 * @param {T[]} arr
 */
export const getLast = (arr) => arr[arr.length - 1];

/**
 * @template T
 * @param {T[]} arr
 */
export const getFirst = (arr) => arr[0];

/**
 * @param {number} value
 * @param {[start: number, end: number]} domain
 */
export const enforceBounds = (value, domain) => {
    const [start, end] = domain;
    let result = value;
    if (result < start) result = start;
    if (result > end) result = end;
    return result;
};

/**
 * @param {[start: number, end: number]} interval1
 * @param {[start: number, end: number]} interval2
 * @returns {[start: number, end: number]}
 */
export const calculateOverlap = (interval1, interval2) => {
    if (interval1[0] > interval2[0]) {
        return calculateOverlap(interval2, interval1);
    }
    const [start1, end1] = interval1;
    const [start2, end2] = interval2;

    if (end1 > start2) {
        return [start2, end1];
    } else {
        return null;
    }
};

/**
 * Truncates the given array using the given interval. See tests for spec
 * @template T
 * @param {T[]} array array of numbers in ascending order or array of objects whose indexKeys' are in ascending order
 * @param {[start: number, end: number]} interval
 * @param {string} [indexKey] item[indexKey] should return a number
 * @returns {T[]}
 */
export const truncate = (array, interval, indexKey) => {
    const [_, end] = interval;
    const getIndex = indexKey ? (item) => item[indexKey] : (item) => item;
    const setIndex = indexKey ? (item, index) => ({ ...item, [indexKey]: index }) : (item, index) => index;

    const truncated = pairwise(array)
        .map(([current, next]) => {
            const startIndex = getIndex(current);
            const endIndex = getIndex(next);
            const overlap = calculateOverlap([startIndex, endIndex], interval);
            return overlap ? setIndex(current, overlap[0]) : null;
        })
        .filter((val) => val !== null);

    const firstItem = getFirst(array);
    const lastItem = getLast(array);
    if (calculateOverlap([getIndex(firstItem), getIndex(lastItem)], interval)) {
        const lastIndex = Math.min(end, getIndex(lastItem));
        truncated.push(setIndex(lastItem, lastIndex));
    }
    return truncated;
};
